/* Pen Tech – cart that sells (v26).
   Custom elements used by snippets/pt-cart-extras.liquid. They live inside the
   cart regions Minimog re-renders after every change, so all state is rebuilt in
   connectedCallback:
   - <pt-ship-bar>  animates the free-shipping bar from its last value and
                    announces the new message to screen readers;
   - <pt-cart-wa>   "Send your order on WhatsApp": the order lines come from the
                    Liquid that was just rendered (always in step with the cart);
                    on click it adds the governorate (window.PTDelivery), the
                    payment line and a WA- reference made from the cart token
                    (/cart.js, kept fresh by Minimog's 'cart:updated');
   - <pt-cart-add>  quick add for «Complete your setup» through Minimog's own
                    cart pipeline (cart:grouped-sections + cartUpdate event). */
(function () {
  'use strict';
  if (window.PTCartExtras) return;

  var tokenCache = '';
  var inflight = null;
  var savedRef = '';
  var lastPct = null;
  var lastBarText = null;

  function settings() { return window.MinimogSettings || {}; }
  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function routes() { return settings().routes || {}; }
  function cartUrl() { return routes().cart || root() + 'cart'; }
  function noop() {}

  function reduceMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  /* one polite live region; messages queued in the same tick are read together */
  var pending = [];
  var pendingTimer = null;
  function announce(text) {
    if (!text) return;
    var r = document.getElementById('pt-cart-live');
    if (!r) {
      r = document.createElement('div');
      r.id = 'pt-cart-live';
      r.className = 'pt-cart-sr';
      r.setAttribute('role', 'status');
      r.setAttribute('aria-live', 'polite');
      document.body.appendChild(r);
    }
    pending.push(text);
    if (pendingTimer) return;
    r.textContent = '';
    pendingTimer = window.setTimeout(function () {
      r.textContent = pending.join(' · ');
      pending = [];
      pendingTimer = null;
    }, 80);
  }

  /* ---------- cart token (for the WA- reference) ---------- */
  function keepToken(c) {
    if (c && typeof c.token === 'string' && c.token) tokenCache = c.token;
    return c;
  }

  function fetchCart() {
    if (inflight) return inflight;
    inflight = fetch(cartUrl() + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('cart ' + r.status)); })
      .then(keepToken)
      .then(function (c) { inflight = null; return c; }, function (err) { inflight = null; throw err; });
    return inflight;
  }

  function warmToken() {
    if (!tokenCache) fetchCart().catch(noop);
  }

  /* Minimog fires this after every /cart/change (full cart JSON, token included) */
  document.addEventListener('cart:updated', function (e) {
    keepToken(e && e.detail && e.detail.cart);
  });

  function cookieToken() {
    try {
      var m = /(?:^|;\s*)cart=([^;]+)/.exec(document.cookie || '');
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  function refOf(token) {
    var base = String(token || '').split('?')[0].replace(/[^a-z0-9]/gi, '');
    return base ? 'WA-' + base.slice(-6).toUpperCase() : '';
  }

  function fill(tpl, token, value) {
    return String(tpl || '').replace(token, function () { return String(value); });
  }

  function saveRef(ref) {
    if (!ref || ref === savedRef) return;
    savedRef = ref;
    var url = (routes().cart_update_url || root() + 'cart/update') + '.js';
    try {
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { wa_ref: ref } })
      }).catch(function () { savedRef = ''; });
    } catch (e) { savedRef = ''; }
  }

  /* ---------- free-shipping bar ---------- */
  class PTShipBar extends HTMLElement {
    connectedCallback() {
      var bar = this.querySelector('[data-fill]');
      var to = Math.max(0, Math.min(100, parseFloat(this.dataset.pct) || 0));
      var textEl = this.querySelector('.pt-shipbar__text');
      var text = textEl ? textEl.textContent.replace(/\s+/g, ' ').trim() : '';
      if (bar && lastPct !== null && lastPct !== to && !reduceMotion()) {
        bar.style.transition = 'none';
        bar.style.width = lastPct + '%';
        void bar.offsetWidth;
        bar.style.transition = '';
        bar.style.width = to + '%';
      }
      if (lastBarText !== null && text && text !== lastBarText) announce(text);
      lastPct = to;
      lastBarText = text;
    }
  }

  /* ---------- send the cart on WhatsApp ---------- */
  class PTCartWa extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('a');
      if (!this.link) return;
      this._onClick = this.onClick.bind(this);
      this.link.addEventListener('click', this._onClick);
      this.link.addEventListener('pointerdown', warmToken);
      this.link.addEventListener('focus', warmToken);
      var self = this;
      if (tokenCache) return;
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          if (entries.some(function (en) { return en.isIntersecting; })) {
            warmToken();
            if (self._io) { self._io.disconnect(); self._io = null; }
          }
        });
        this._io.observe(this);
      }
    }

    disconnectedCallback() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this.link) {
        this.link.removeEventListener('click', this._onClick);
        this.link.removeEventListener('pointerdown', warmToken);
        this.link.removeEventListener('focus', warmToken);
      }
    }

    build(ref) {
      var d = this.dataset;
      var base = '';
      try { base = JSON.parse(d.lines || '""'); } catch (e) { base = ''; }
      var lines = [String(base || '')];
      var gov = '';
      try {
        var D = window.PTDelivery;
        gov = D && typeof D.govLabel === 'function' ? String(D.govLabel() || '') : '';
      } catch (e) { gov = ''; }
      if (gov && d.gov) lines.push(fill(d.gov, '%G%', gov));
      if (d.pay) lines.push(d.pay);
      if (ref && d.ref) lines.push(fill(d.ref, '%R%', ref));
      return lines.filter(Boolean).join('\n');
    }

    onClick() {
      var d = this.dataset;
      var num = String(d.number || '').replace(/\D/g, '');
      var ref = refOf(tokenCache || cookieToken());
      /* without a number or lines, the server-rendered href (lines + subtotal + payment) is used as is */
      if (num && d.lines) {
        this.link.href = 'https://wa.me/' + num + '?text=' + encodeURIComponent(this.build(ref));
        saveRef(ref);
      }
      if (!tokenCache) warmToken();
      var total = parseInt(d.total, 10) || 0;
      document.dispatchEvent(new CustomEvent('pt:wa-cart', { detail: { ref: ref, total: total } }));
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('pt_whatsapp_cart', {
            ref: ref,
            value: total / 100,
            currency: d.currency || settings().currency_code || ''
          });
        }
      } catch (e) { /* analytics is optional */ }
    }
  }

  /* ---------- «Complete your setup» quick add ---------- */
  function focusAfterAdd(inDrawer) {
    /* the add button is gone after the re-render: keep focus near where it was */
    var scope = document.getElementById(inDrawer ? 'MinimogCartDrawer' : 'MinimogCart') || document;
    var target = scope.querySelector('.pt-setup__heading') ||
      (inDrawer ? scope.querySelector('.m-cart-drawer__title') : document.querySelector('main h1, #MainContent h1'));
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
  }

  class PTCartAdd extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      if (!this.btn) return;
      this._onClick = this.add.bind(this);
      this.btn.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      if (this.btn && this._onClick) this.btn.removeEventListener('click', this._onClick);
    }

    setBusy(on) {
      if (!this.btn) return;
      this.busy = on;
      /* aria-disabled, not disabled: a disabled button drops keyboard focus to <body>,
         and on an error the shopper must stay on «Add» */
      if (on) {
        this.btn.setAttribute('aria-disabled', 'true');
        this.btn.setAttribute('aria-busy', 'true');
      } else {
        this.btn.removeAttribute('aria-disabled');
        this.btn.removeAttribute('aria-busy');
      }
    }

    message(text) {
      var box = this.closest('.pt-setup');
      var out = box && box.querySelector('[data-pt-msg]');
      if (out) out.textContent = text;
    }

    fail(text) {
      var box = this.closest('.pt-setup');
      var out = box && box.querySelector('[data-pt-msg]');
      this.message(text || (out && out.getAttribute('data-error')) || '');
    }

    add(e) {
      if (e) e.preventDefault();
      var id = this.dataset.variantId;
      if (!id || this.busy) return;
      var events = window.MinimogEvents;
      var theme = window.MinimogTheme;
      var inDrawer = !!this.closest('m-cart-drawer');
      var sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
      );
      var body = new FormData();
      body.append('id', id);
      body.append('quantity', '1');
      if (sections.length) {
        body.append('sections', sections);
        body.append('sections_url', window.location.pathname);
      }
      var self = this;
      this.message('');
      this.setBusy(true);
      fetch(routes().cart_add_url || root() + 'cart/add', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) {
            self.setBusy(false);
            self.fail(res.description || res.message);
            return;
          }
          if (events && theme && theme.pubSubEvents && sections.length && res && res.sections) {
            /* queued first, so it is read before the new free-shipping message */
            announce((window.MinimogStrings && window.MinimogStrings.itemAdded) || '');
            events.emit(theme.pubSubEvents.cartUpdate, { cart: res, productVariantId: id });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: res } }));
            focusAfterAdd(inDrawer);
          } else {
            window.location.href = cartUrl();
          }
        })
        .catch(function () {
          self.setBusy(false);
          self.fail();
        })
        .then(function () {
          if (self.isConnected && self.busy) self.setBusy(false);
        });
    }
  }

  if (!customElements.get('pt-ship-bar')) customElements.define('pt-ship-bar', PTShipBar);
  if (!customElements.get('pt-cart-wa')) customElements.define('pt-cart-wa', PTCartWa);
  if (!customElements.get('pt-cart-add')) customElements.define('pt-cart-add', PTCartAdd);

  window.PTCartExtras = {
    cart: fetchCart,
    ref: function () { return refOf(tokenCache || cookieToken()); }
  };
})();
